import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Circle,
  FileSignature,
  Receipt,
  Sparkles,
  Wrench,
  X,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  IconChip,
  Skeleton,
} from '@/components/ui';
import { request } from '@/lib/api';
import { useCanEdit } from '@/lib/auth';
import { useFeatures, type Feature } from '@/lib/features';
import { intlLocale } from '@/lib/i18n';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

/* ---------------------------------------------------------------------------
   תדריך הבוקר — הסיבה לפתוח את המערכת כל יום.

   כל שורה מובילה לפעולה, וכל סכום אומר אם הוא מדויק או הערכה. שורה
   ריקה לא מוצגת: מסך מלא באפסים מלמד להפסיק להסתכל.
   --------------------------------------------------------------------------- */

const briefSchema = z.object({
  today: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      status: z.string(),
      customerName: z.string(),
      technician: z.string().nullable(),
      scheduledStart: z.string(),
      scheduledEnd: z.string().nullable(),
      confirmed: z.boolean(),
      onTheWay: z.boolean(),
    }),
  ),
  needsReply: z.object({
    reschedules: z.array(
      z.object({
        taskId: z.string().uuid(),
        title: z.string(),
        customerName: z.string(),
        note: z.string().nullable(),
      }),
    ),
    expiringQuotes: z.array(
      z.object({
        quoteId: z.string().uuid(),
        quoteNumber: z.number().int(),
        customerName: z.string(),
        totalAmount: z.string(),
        validUntil: z.string(),
      }),
    ),
  }),
  moneyWaiting: z.object({
    approvedUnscheduled: z.object({ count: z.number().int(), amount: z.string() }),
    closedUnbilled: z.object({ count: z.number().int(), estimatedAmount: z.string() }),
    maintenanceDue: z.object({ count: z.number().int(), estimatedAmount: z.string().nullable() }),
  }),
  week: z.object({
    bookedByCustomers: z.number().int(),
    quotesApproved: z.number().int(),
    quotesApprovedAmount: z.string(),
    visitsConfirmed: z.number().int(),
    statusLinksSent: z.number().int(),
    jobsClosed: z.number().int(),
  }),
});
export type Brief = z.infer<typeof briefSchema>;

const activationSchema = z.object({
  steps: z.array(
    z.object({ key: z.enum(['priceList', 'equipment', 'statusLink', 'quote']), done: z.boolean() }),
  ),
  completed: z.number().int(),
  total: z.number().int(),
  allDone: z.boolean(),
});
export type Activation = z.infer<typeof activationSchema>;

const timeFmt = new Intl.DateTimeFormat(intlLocale(), {
  timeZone: 'Asia/Jerusalem',
  hour: '2-digit',
  minute: '2-digit',
});

export function MorningBrief() {
  const brief = useQuery({
    queryKey: ['dashboard', 'brief'],
    queryFn: ({ signal }) => request('/dashboard/brief', { schema: briefSchema, signal }),
  });
  const activation = useQuery({
    queryKey: ['dashboard', 'activation'],
    queryFn: ({ signal }) => request('/dashboard/activation', { schema: activationSchema, signal }),
    // צעד שהושלם חייב להיראות מסומן מיד. ברירת המחדל (30 שניות) הציגה
    // רשימה ישנה אחרי שהמשתמש בדיוק סיים את הצעד.
    staleTime: 0,
  });

  return (
    <div className="space-y-4">
      {activation.data && !activation.data.allDone ? (
        <ActivationChecklist activation={activation.data} />
      ) : null}
      {brief.isLoading ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : brief.isError ? (
        <Card>
          <ErrorState error={brief.error} onRetry={() => void brief.refetch()} />
        </Card>
      ) : brief.data ? (
        <BriefView brief={brief.data} />
      ) : null}
    </div>
  );
}

export function BriefView({ brief }: { brief: Brief }) {
  const { t } = useTranslation('dashboard');
  const features = useFeatures();
  const { today, needsReply, moneyWaiting: m, week } = brief;
  const replies =
    needsReply.reschedules.length + (features.has('quotes') ? needsReply.expiringQuotes.length : 0);
  // רק מה שהעסק משתמש בו: בלי חשבוניות אין "עבודה שלא חויבה", בלי ציוד אין "מגיע לטיפול".
  const money = {
    quotes: features.has('quotes') ? m.approvedUnscheduled.count : 0,
    unbilled: features.has('invoicing') ? m.closedUnbilled.count : 0,
    maintenance: features.has('equipment') ? m.maintenanceDue.count : 0,
  };
  const moneyRelevant =
    features.has('quotes') || features.has('invoicing') || features.has('equipment');
  const hasMoney = money.quotes + money.unbilled + money.maintenance > 0;

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-3">
        {features.has('scheduling') || features.has('customers') ? (
          <Card>
            <CardHeader tone="accent">
              <CardTitle className="flex items-center gap-2.5">
                <IconChip icon={CalendarDays} tone="accent" variant="solid" size="sm" />
                {t('brief.today.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              {today.length === 0 ? (
                <p className="text-sm text-fg-muted">{t('brief.today.empty')}</p>
              ) : (
                <ul className="space-y-1">
                  {today.map((v) => (
                    <li key={v.id}>
                      <Link
                        to="/tasks/$taskId"
                        params={{ taskId: v.id }}
                        className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-hover"
                      >
                        <span
                          className="tabular w-12 shrink-0 text-sm font-medium text-fg"
                          dir="ltr"
                        >
                          {timeFmt.format(new Date(v.scheduledStart))}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-fg">{v.customerName}</span>
                          <span className="block truncate text-xs text-fg-muted">
                            {v.technician ?? t('brief.today.unassigned')}
                          </span>
                        </span>
                        {v.status === 'CLOSED' ? (
                          <Badge tone="success">{t('brief.today.done')}</Badge>
                        ) : v.onTheWay ? (
                          <Badge tone="info">{t('brief.today.onTheWay')}</Badge>
                        ) : v.confirmed ? (
                          <Badge tone="success">{t('brief.today.confirmed')}</Badge>
                        ) : (
                          <Badge tone="neutral">{t('brief.today.notConfirmed')}</Badge>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}

        {features.has('customers') ? (
          <Card className={cn(replies > 0 && 'border-warning-border')}>
            <CardHeader tone="orange">
              <CardTitle className="flex items-center gap-2.5">
                <IconChip icon={CalendarClock} tone="orange" variant="solid" size="sm" />
                {t('brief.needsReply.title')}
                {replies > 0 ? <Badge tone="warning">{replies}</Badge> : null}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              {replies === 0 ? (
                <p className="text-sm text-fg-muted">{t('brief.needsReply.empty')}</p>
              ) : (
                <ul className="space-y-1">
                  {needsReply.reschedules.map((r) => (
                    <li key={r.taskId}>
                      <Link
                        to="/tasks/$taskId"
                        params={{ taskId: r.taskId }}
                        className="-mx-2 block rounded-lg px-2 py-2 hover:bg-surface-hover"
                      >
                        <span className="block text-sm text-fg">
                          {t('brief.needsReply.reschedule', { name: r.customerName })}
                        </span>
                        {r.note ? (
                          <span className="block truncate text-xs text-fg-muted">"{r.note}"</span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                  {(features.has('quotes') ? needsReply.expiringQuotes : []).map((q) => (
                    <li key={q.quoteId}>
                      <Link
                        to="/quotes"
                        className="-mx-2 block rounded-lg px-2 py-2 hover:bg-surface-hover"
                      >
                        <span className="block text-sm text-fg">
                          {t('brief.needsReply.quoteExpiring', {
                            number: q.quoteNumber,
                            name: q.customerName,
                          })}
                        </span>
                        <span className="tabular block text-xs text-fg-muted">
                          {formatCurrency(q.totalAmount)} · {t('brief.needsReply.worthReminder')}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}

        {moneyRelevant ? (
          <Card className={cn(hasMoney && 'border-accent-border')}>
            <CardHeader tone="teal">
              <CardTitle className="flex items-center gap-2.5">
                <IconChip icon={Receipt} tone="teal" variant="solid" size="sm" />
                {t('brief.money.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              {!hasMoney ? (
                <p className="text-sm text-fg-muted">{t('brief.money.empty')}</p>
              ) : (
                <ul className="space-y-1">
                  {money.quotes > 0 ? (
                    <MoneyRow
                      to="/quotes"
                      icon={FileSignature}
                      label={plural(
                        t,
                        'brief.money.approvedUnscheduled',
                        m.approvedUnscheduled.count,
                      )}
                      amount={m.approvedUnscheduled.amount}
                    />
                  ) : null}
                  {money.unbilled > 0 ? (
                    <MoneyRow
                      to="/invoices"
                      icon={Receipt}
                      label={plural(t, 'brief.money.closedUnbilled', m.closedUnbilled.count)}
                      amount={m.closedUnbilled.estimatedAmount}
                      estimate
                    />
                  ) : null}
                  {money.maintenance > 0 ? (
                    <MoneyRow
                      to="/maintenance"
                      icon={Wrench}
                      label={plural(t, 'brief.money.maintenanceDue', m.maintenanceDue.count)}
                      amount={m.maintenanceDue.estimatedAmount}
                      estimate
                    />
                  ) : null}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>

      <WeeklyValue week={week} />
    </>
  );
}

function MoneyRow({
  to,
  icon: Icon,
  label,
  amount,
  estimate,
}: {
  to: string;
  icon: typeof Receipt;
  label: string;
  amount: string | null;
  estimate?: boolean;
}) {
  const { t } = useTranslation('dashboard');
  return (
    <li>
      <Link
        to={to}
        className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-hover"
      >
        <Icon className="size-4 shrink-0 text-teal" aria-hidden />
        <span className="min-w-0 flex-1 text-sm text-fg">{label}</span>
        {amount !== null ? (
          <span className="tabular shrink-0 text-sm font-semibold text-fg">
            {estimate ? (
              <span className="font-normal text-fg-subtle">{t('brief.money.approx')}</span>
            ) : null}
            {formatCurrency(amount)}
          </span>
        ) : null}
      </Link>
    </li>
  );
}

export function WeeklyValue({ week: w }: { week: Brief['week'] }) {
  const { t } = useTranslation('dashboard');
  const items = [
    {
      n: w.bookedByCustomers,
      text: plural(t, 'brief.week.bookedByCustomers', w.bookedByCustomers),
    },
    {
      n: w.quotesApproved,
      text: `${plural(t, 'brief.week.quotesApproved', w.quotesApproved)} (${formatCurrency(w.quotesApprovedAmount)})`,
    },
    { n: w.visitsConfirmed, text: plural(t, 'brief.week.visitsConfirmed', w.visitsConfirmed) },
    { n: w.statusLinksSent, text: plural(t, 'brief.week.statusLinksSent', w.statusLinksSent) },
    { n: w.jobsClosed, text: plural(t, 'brief.week.jobsClosed', w.jobsClosed) },
  ].filter((i) => i.n > 0);

  if (items.length === 0) return null;

  return (
    <Card className="border-violet-border/70 bg-linear-to-br from-violet-subtle/80 via-surface-raised to-accent-subtle/60">
      <CardHeader className="border-violet-border/50">
        <CardTitle className="flex items-center gap-2.5">
          <IconChip icon={Sparkles} tone="violet" variant="solid" size="sm" />
          {t('brief.week.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-3">
        <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {items.map((i) => (
            <li key={i.text} className="flex items-baseline gap-2 text-sm text-fg">
              <CheckCircle2
                className="size-3.5 shrink-0 translate-y-0.5 text-success"
                aria-hidden
              />
              {i.text}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// הכותרת וההסבר — dashboard:activation.steps.<key>.{title,why}.
const STEPS: Record<Activation['steps'][number]['key'], { to: string }> = {
  priceList: { to: '/price-list' },
  equipment: { to: '/customers' },
  statusLink: { to: '/tasks' },
  quote: { to: '/quotes' },
};

const STEP_FEATURE: Record<Activation['steps'][number]['key'], Feature> = {
  priceList: 'priceList',
  equipment: 'equipment',
  statusLink: 'customers',
  quote: 'quotes',
};

const HIDE_KEY = 'craftmind.activation.hidden';

export function ActivationChecklist({ activation }: { activation: Activation }) {
  const { t } = useTranslation('dashboard');
  const features = useFeatures();
  const canEdit = useCanEdit();
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === '1';
    } catch {
      return false;
    }
  });
  // צעדי הקמה (מחירון, ציוד, הצעה) — לא רלוונטי למי שצופה בלבד.
  if (hidden || !canEdit) return null;

  // צעדים של מודולים כבויים (הצעת מחיר אצל מנהלת משרד) לא נספרים.
  const steps = activation.steps.filter((s) => features.has(STEP_FEATURE[s.key]));
  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  if (total === 0) return null;
  const next = steps.find((s) => !s.done)?.key;
  const pct = Math.round((completed / total) * 100);

  return (
    <Card>
      <CardHeader tone="accent" className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{t('activation.title')}</CardTitle>
          <p className="mt-1 text-xs text-fg-muted">
            {t('activation.progress', { completed, total })}
          </p>
        </div>
        <button
          type="button"
          aria-label={t('activation.hide')}
          className="grid size-8 place-items-center rounded-md text-fg-muted hover:bg-surface-hover hover:text-fg"
          onClick={() => {
            try {
              localStorage.setItem(HIDE_KEY, '1');
            } catch {
              /* נוחות בלבד */
            }
            setHidden(true);
          }}
        >
          <X className="size-4" aria-hidden />
        </button>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        <div
          className="h-2 overflow-hidden rounded-full bg-accent-subtle"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('activation.progressLabel')}
        >
          <div
            className="bg-brand-gradient h-full rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => {
            const step = STEPS[s.key];
            return (
              <li key={s.key}>
                <Link
                  to={step.to}
                  className={cn(
                    'flex h-full gap-2.5 rounded-lg border p-3 transition-colors',
                    s.done
                      ? 'border-success-border/60 bg-success-subtle/50'
                      : s.key === next
                        ? 'border-accent bg-accent-subtle shadow-sm hover:brightness-105'
                        : 'border-border hover:bg-surface-hover',
                  )}
                >
                  {s.done ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden />
                  )}
                  <span>
                    <span
                      className={cn(
                        'block text-sm font-medium',
                        s.done ? 'text-fg-muted line-through' : 'text-fg',
                      )}
                    >
                      {t(`activation.steps.${s.key}.title`)}
                    </span>
                    {!s.done ? (
                      <span className="mt-0.5 block text-xs text-fg-muted">
                        {t(`activation.steps.${s.key}.why`)}
                      </span>
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

type PluralKey =
  | 'brief.money.approvedUnscheduled'
  | 'brief.money.closedUnbilled'
  | 'brief.money.maintenanceDue'
  | 'brief.week.bookedByCustomers'
  | 'brief.week.quotesApproved'
  | 'brief.week.visitsConfirmed'
  | 'brief.week.statusLinksSent'
  | 'brief.week.jobsClosed';

/** אחד — בלי מספר ("עבודה נסגרה"); יותר — עם המספר מעוצב ("3 עבודות נסגרו"). */
function plural(t: TFunction<'dashboard'>, key: PluralKey, n: number): string {
  return t(key, { count: n, n: formatNumber(n) });
}
